import clsx from 'clsx';
import { resolveUrl } from '../../../../utils/url';
import { FACE_CROP_PADDING_RATIO } from '../../../../utils/constants';
import styles from './FaceThumbnailGrid.module.css';

const THUMB_SIZE = 48;

/**
 * Grid of face thumbnails with CSS-based cropping.
 *
 * Single responsibility: Display face thumbnails in a grid.
 * Does not contain business logic - receives all state via props.
 */
export default function FaceThumbnailGrid({
  faces,
  selectedFaceId,
  hoveredFaceId,
  imgDims,
  previewUrl,
  onSelectFace,
  onHoverFace,
  onIgnore,
}) {
  if (!faces || faces.length === 0) {
    return (
      <div className={styles.grid}>
        <div className={styles.empty}>No faces</div>
      </div>
    );
  }

  return (
    <div className={styles.grid}>
      {faces.map((face) => (
        <FaceThumbnail
          key={face.id}
          face={face}
          imgDims={imgDims}
          previewUrl={previewUrl}
          isSelected={selectedFaceId === face.id}
          isHovered={hoveredFaceId === face.id}
          onClick={() => onSelectFace(face)}
          onMouseEnter={() => onHoverFace(face)}
          onMouseLeave={() => onHoverFace(null)}
          onContextMenu={onIgnore ? () => onIgnore(face) : undefined}
        />
      ))}
    </div>
  );
}

/**
 * Individual face thumbnail with CSS-based cropping.
 */
function FaceThumbnail({
  face,
  imgDims,
  previewUrl,
  isSelected,
  isHovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onContextMenu,
}) {
  const vizImg = previewUrl || face.viz_url;
  const thumb = face.thumbnail || face.preview_url;
  const isLabeled = !!(face.person || face.person_display || face.person_name);

  // Calculate CSS-based face crop using frame_bbox (full-frame coords)
  const rawBbox = face.frame_bbox || face.bbox;
  const faceImgW = face.image_width || imgDims?.w || 1;
  const faceImgH = face.image_height || imgDims?.h || 1;

  let cropStyle = null;

  // Only use CSS crop if we have viz_url (full-frame)
  if (rawBbox?.length === 4 && vizImg) {
    const [x1, y1, x2, y2] = rawBbox;
    const bw = x2 - x1;
    const bh = y2 - y1;
    const pad = Math.max(bw, bh) * FACE_CROP_PADDING_RATIO;
    const cx1 = Math.max(0, x1 - pad);
    const cy1 = Math.max(0, y1 - pad);
    const cx2 = Math.min(faceImgW, x2 + pad);
    const cy2 = Math.min(faceImgH, y2 + pad);
    const cropW = cx2 - cx1;
    const cropH = cy2 - cy1;
    const scale = THUMB_SIZE / Math.max(cropW, cropH);
    const bgW = faceImgW * scale;
    const bgH = faceImgH * scale;
    const bgX = -cx1 * scale;
    const bgY = -cy1 * scale;

    cropStyle = {
      width: THUMB_SIZE,
      height: THUMB_SIZE,
      backgroundImage: `url(${resolveUrl(vizImg)})`,
      backgroundSize: `${bgW}px ${bgH}px`,
      backgroundPosition: `${bgX}px ${bgY}px`,
      backgroundRepeat: 'no-repeat',
    };
  }

  return (
    <div
      className={clsx(
        styles.face,
        isSelected && styles.selected,
        isHovered && styles.hovered
      )}
      onClick={onClick}
      onContextMenu={(e) => {
        if (onContextMenu && !isLabeled) {
          e.preventDefault();
          onContextMenu();
        }
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {cropStyle ? (
        <div
          className={styles.thumb}
          style={cropStyle}
          title={`Face ${face.player_index ?? '?'}`}
        />
      ) : thumb ? (
        <img
          className={styles.thumbImg}
          src={resolveUrl(thumb)}
          alt={`Face ${face.player_index ?? '?'}`}
        />
      ) : (
        <div className={styles.thumbFallback}>?</div>
      )}

      <div className={styles.label}>
        <span className={isLabeled ? styles.indexLabeled : styles.indexUnlabeled}>
          #{face.player_index ?? '?'}
        </span>
        <div className={styles.name}>
          {face.person_display || face.person_name || 'Unlabeled'}
        </div>
      </div>
    </div>
  );
}
